<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useAuth } from '@/composables/useAuth'
import { useEvents } from '@/composables/useEvents'
import { useToasts } from '@/composables/useToasts'
import EntryModal from './EntryModal.vue'
import PublishModal from './PublishModal.vue'
import type { Entry } from '@/types'

const { t } = useI18n()
const { isAuthenticated } = useAuth()
const { allEntries, loadEvents } = useEvents()
const { showToast } = useToasts()

// Entry modal state
const entryModalVisible = ref(false)
const editingEntry = ref<Entry | null>(null)

// Publish modal state
const publishModalVisible = ref(false)
const publishingEntryId = ref('')

function openNewEntry() {
  editingEntry.value = null
  entryModalVisible.value = true
  document.body.style.overflow = 'hidden'
}

function openEditEntry(entry: Entry) {
  editingEntry.value = entry
  entryModalVisible.value = true
  document.body.style.overflow = 'hidden'
}

function closeEntryModal() {
  entryModalVisible.value = false
  document.body.style.overflow = ''
}

async function onEntrySaved() {
  await loadEvents()
}

function openPublishModal(entryId: string) {
  publishingEntryId.value = entryId
  publishModalVisible.value = true
}

function closePublishModal() {
  publishModalVisible.value = false
}

async function onPublished() {
  publishModalVisible.value = false
  await loadEvents()
}

async function confirmAndDelete(id: string) {
  if (!window.confirm('Delete this event?')) return

  try {
    const res = await fetch(`/api/admin/events/${id}`, { method: 'DELETE' })
    if (res.ok) {
      showToast('Event deleted.', 'success')
      await loadEvents()
    } else {
      const data = await res.json().catch(() => ({}))
      showToast(data.message || 'Delete failed.')
    }
  } catch {
    showToast('Delete failed.')
  }
}

// Expose methods for parent components to call
defineExpose({
  openNewEntry,
  openEditEntry,
  openPublishModal,
  confirmAndDelete,
})
</script>

<template>
  <EntryModal
    :visible="entryModalVisible"
    :entry="editingEntry"
    @close="closeEntryModal"
    @saved="onEntrySaved"
  />
  <PublishModal
    :visible="publishModalVisible"
    :entry-id="publishingEntryId"
    @close="closePublishModal"
    @published="onPublished"
  />
</template>
